import { useMemo } from 'react';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { FormattedMessage } from 'react-intl';
import { PinCodeInput, PseudoInputEvent } from '../../atoms/PinCodeInput/PinCodeInput';
import { ProviderField } from '../../../models/provider';
import { SubmitProviderInputsPayload } from '../../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs';
import { ContentBody } from '../../atoms/ContentBody/ContentBody';
import { Footer } from '../../atoms/Footer/Footer';
import { PageView, UserInteractionEvent } from '../../../models/analytics';
import { Caption } from '../../atoms/Caption/Caption';
import { FormLabel } from '../../atoms/Label/FormLabel';
import { usePageViewEvents } from '../../../core/Analytics/usePageViewEvents';
import { TemplateOrderedList } from '../molecules/TemplateOrderedList/TemplateOrderedList';
import { TemplateElementStyle, TemplateElementType, Templates } from '../templates';
import { useProviderInput } from '../useProviderInput';
import { MaybeChangeMethodButton } from '../MaybeChangeMethodButton/MaybeChangeMethodButton';
import { Text } from '../../atoms/Text/Text';
import { H1 } from '../../atoms/Heading/Heading';
import { TextAlignmentContainer } from '../../TextAlignmentContainer/TextAlignmentContainer';
import { ContentAlignmentContainer } from '../../ContentAlignmentContainer/ContentAlignmentContainer';
import { commonMessages } from '../../atoms/CommonMessages/messages';
import { Button } from '../../atoms/Button/Button';
import { useCredentialsCopy } from '../../screens/SubmitCredentials/hooks/useCredentialsCopy';
import { StyledForm } from '../../screens/SubmitCredentials/molecules/ProviderInputs/ProviderInputs.styles';
import { Accordion } from '../../atoms/Accordion/Accordion';
import { StyledPinCodeContainer } from './SmsCodeTemplate.styles';
import { messages } from './messages';

type FormValue = {
  name: string;
  value: string;
};

export type SmsCodeTemplateProps = {
  fields: ProviderField[];
  onSubmit: (values: SubmitProviderInputsPayload) => void;
};

export const SmsCodeTemplate = ({ fields, onSubmit }: SmsCodeTemplateProps) => {
  const { sendViewInteractionEvent, sendViewInteractionEventOnce } = usePageViewEvents({
    pageView: PageView.SMS_CODE_TEMPLATE_SCREEN,
  });

  const handleSubmitWithAnalytics = (values: SubmitProviderInputsPayload) => {
    sendViewInteractionEvent({ interaction: UserInteractionEvent.SUBMIT_SUPPLEMENTAL_INFORMATION });
    onSubmit(values);
  };

  const handleToggle = (label: string) => {
    sendViewInteractionEventOnce({ interaction: UserInteractionEvent.TOGGLE_ACCORDION, label });
  };

  const { formState, updateField } = useProviderInput(fields);

  const isAllInputsValid = useMemo(
    () =>
      Object.values(formState).length &&
      Object.values(formState).every((formField) => formField.isValid),
    [formState]
  );
  const inputValues = useMemo(
    () =>
      Object.keys(formState).reduce<Record<string, string>>((values, fieldName) => {
        values[fieldName] = formState[fieldName].value;

        return values;
      }, {}),
    [formState]
  );

  const { body, inputPinCode, orderedList } = useMemo(() => {
    return {
      body: fields.find(
        (el) => el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.TITLE
      ),
      inputPinCode: fields.find((el) => el.type === TemplateElementType.INPUT),
      orderedList: fields.find(
        (el) =>
          el.type === TemplateElementType.TEXT && el.style === TemplateElementStyle.ORDERED_LIST
      ),
    };
  }, [fields]);

  const { heading } = useCredentialsCopy();

  const validateInput = ({ name, value }: FormValue) => {
    updateField(name, value);
  };

  return (
    <ContentBody data-testid="smscode-template">
      <Box>
        <TextAlignmentContainer>
          <H1 mb="xxsmall">{heading}</H1>

          <Text color="secondary" mb="large">
            {body ? body.value : <FormattedMessage {...messages.helpText} />}
          </Text>
        </TextAlignmentContainer>

        {inputPinCode && inputPinCode.maxLength && (
          <ContentAlignmentContainer>
            <StyledForm
              id={Templates.SMS_CODE}
              onSubmit={() => handleSubmitWithAnalytics({ ...inputValues })}
              data-testid={Templates.SMS_CODE}
            >
              {inputPinCode.description && (
                <FormLabel mb="xxsmall">{inputPinCode.description}</FormLabel>
              )}
              <StyledPinCodeContainer>
                <PinCodeInput
                  onChange={(e: PseudoInputEvent) =>
                    validateInput({
                      name: inputPinCode.name,
                      value: e.target.value,
                    })
                  }
                  ariaLabel={inputPinCode.description}
                  name="PinCodeInput"
                  type={inputPinCode.numeric ? 'tel' : 'text'}
                  autoComplete="one-time-code"
                  inputLength={inputPinCode?.maxLength}
                  inputMode={inputPinCode.numeric ? 'numeric' : 'text'}
                />
                {inputPinCode.helpText && <Caption message={inputPinCode.helpText} />}
              </StyledPinCodeContainer>
            </StyledForm>
          </ContentAlignmentContainer>
        )}

        {orderedList && (
          <Box mt="large" mb="normal">
            <Accordion
              heading={orderedList.description}
              name="instructions"
              onToggle={handleToggle}
            >
              <TemplateOrderedList field={orderedList} renderDescription={false} />
            </Accordion>
          </Box>
        )}
      </Box>
      <Footer stickToBottom px="none">
        <Button
          mb="normal"
          form={Templates.SMS_CODE}
          disabled={!isAllInputsValid}
          size="large"
          type="submit"
          color="primary"
          variant="contained"
        >
          <FormattedMessage {...commonMessages.continue} />
        </Button>
        <MaybeChangeMethodButton elements={fields} onSubmit={onSubmit} />
      </Footer>
    </ContentBody>
  );
};
